import { useCallback } from 'react';
import { ConnectionHandler, graphql, useMutation } from 'react-relay';
import { useExperienceStart } from '@atlassian/jira-experience-tracker/src/ui/experience-start/index.tsx';
import { useExperienceFail } from '@atlassian/jira-experience-tracker/src/ui/experience-fail/index.tsx';
import { useExperienceSuccess } from '@atlassian/jira-experience-tracker/src/ui/experience-success/index.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import RelayDataID from '@atlassian/relay-data-id';
import { MutationErrorCollection } from '@atlassian/jira-mutation-error/src/mutation-error-collection.tsx';
import { getBoardQueryParams } from '@atlassian/jira-business-board-query-params/src/query-params.tsx';
import type { reorderColumnsMutation } from '@atlassian/jira-relay/src/__generated__/reorderColumnsMutation.graphql.ts';
import messages from './messages.tsx';

const experience = 'setBoardViewColumnsOrder';

export const useReorderColumns = (viewId: string) => {
	const { showFlag } = useFlagsService();

	const startExperience = useExperienceStart({
		experience,
		analyticsSource: 'jiraWorkManagementBoard',
	});
	const markExperienceSuccess = useExperienceSuccess({
		experience,
	});
	const markExperienceFailed = useExperienceFail({
		experience,
	});

	const [commit] = useMutation<reorderColumnsMutation>(graphql`
		mutation reorderColumnsMutation($input: JiraSetBoardViewColumnsOrderInput!) {
			jira_setBoardViewColumnsOrder(input: $input) {
				success
				boardView {
					...BusinessBoard_view
				}
				errors {
					message
					extensions {
						errorType
						statusCode
					}
				}
			}
		}
	`);

	return useCallback(
		(reorderedColumnIds: string[]) => {
			startExperience();

			const handleFailure = (error: Error) => {
				markExperienceFailed('BoardContent jira_setBoardViewColumnsOrder mutation failed', error);
				showFlag({
					messageId:
						'work-management-board.controllers.reorder-columns.show-flag.error.columns-reordered.show-flag.error.ui.board.show-flag.error.set-board-view-columns-order',
					messageType: 'transactional',
					title: messages.setBoardViewColumnsOrderFailedTitle,
					description: messages.setBoardViewColumnsOrderFailedMessage,
					type: 'error',
				});
			};

			commit({
				variables: {
					input: {
						columnIds: reorderedColumnIds,
						viewId,
						settings: getBoardQueryParams(),
					},
				},
				optimisticUpdater: (store) => {
					const relayId = RelayDataID({ id: viewId }, 'JiraBoardView');
					const boardViewRecord = relayId && store.get(relayId);
					const connection =
						boardViewRecord &&
						ConnectionHandler.getConnection(boardViewRecord, 'business_board_columns');
					if (!connection) {
						return;
					}

					const existingEdges = connection.getLinkedRecords('edges') || [];
					const newEdges = reorderedColumnIds
						.map((columnId) =>
							existingEdges.find(
								(edge) => edge?.getLinkedRecord('node')?.getValue('id') === columnId,
							),
						)
						.filter(Boolean);
					connection.setLinkedRecords(newEdges, 'edges');
				},
				onError(error: Error) {
					handleFailure(error);
				},
				onCompleted(response) {
					if (response.jira_setBoardViewColumnsOrder?.success !== true) {
						const error = new MutationErrorCollection(
							response.jira_setBoardViewColumnsOrder?.errors,
						);
						handleFailure(error);
						return;
					}

					markExperienceSuccess();
				},
			});
		},
		[commit, markExperienceFailed, markExperienceSuccess, showFlag, startExperience, viewId],
	);
};
